import React, { useEffect, useState, useCallback } from "react";
import { api, formatBRL, formatDateTimeBR } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { Wallet, TrendingUp, ArrowDownRight, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function Balances() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithV2Fallback("/v2/balances", null);
      setData(result.data);
      setSource(result.source);
    } catch (e) {
      setError("Erro ao carregar saldos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const balances = data?.data || data?.balances || (Array.isArray(data) ? data : []);
  const summary = data?.summary || null;

  return (
    <div className="space-y-6" data-testid="balances-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Financeiro</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Saldos</h1>
          <p className="mt-1 text-sm text-zinc-500">Posição consolidada de saldos e disponibilidades.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="balances-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="balances-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={`bal-sk-${i}`} className="h-32 rounded-lg" />)}
        </div>
      ) : balances.length === 0 && !summary ? (
        <div className="zemo-card p-12 text-center" data-testid="balances-empty">
          <Wallet className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum saldo disponível</p>
          <p className="text-xs text-zinc-400 mt-1">Os saldos aparecerão quando houver operações ativas.</p>
        </div>
      ) : (
        <>
          {summary && (
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="zemo-card p-5" data-testid="balance-total">
                <div className="flex items-center justify-between">
                  <span className="zemo-overline">Saldo Total</span>
                  <Wallet className="h-4 w-4 text-zinc-400" />
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight font-mono-num">{formatBRL(summary.total || 0)}</div>
              </div>
              <div className="zemo-card p-5" data-testid="balance-available">
                <div className="flex items-center justify-between">
                  <span className="zemo-overline">Disponível</span>
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight font-mono-num text-emerald-700">{formatBRL(summary.available || 0)}</div>
              </div>
              <div className="zemo-card p-5" data-testid="balance-blocked">
                <div className="flex items-center justify-between">
                  <span className="zemo-overline">Bloqueado</span>
                  <ArrowDownRight className="h-4 w-4 text-amber-500" />
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight font-mono-num text-amber-700">{formatBRL(summary.blocked || 0)}</div>
              </div>
            </div>
          )}

          {balances.length > 0 && (
            <div className="zemo-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">Descrição</th>
                      <th className="text-right px-5 py-3 font-semibold">Valor</th>
                      <th className="text-left px-5 py-3 font-semibold">Tipo</th>
                      <th className="text-left px-5 py-3 font-semibold">Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="balances-table">
                    {balances.map((b, i) => (
                      <tr key={b.id || i} className="hover:bg-zinc-50/60">
                        <td className="px-5 py-3.5 text-zinc-900 font-medium">{b.description || b.label || "—"}</td>
                        <td className="px-5 py-3.5 text-right font-semibold font-mono-num">{formatBRL(b.amount || b.value || 0)}</td>
                        <td className="px-5 py-3.5 text-zinc-600">{b.type || "—"}</td>
                        <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateTimeBR(b.date || b.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {source === "v2" && (
        <div className="text-[11px] text-zinc-400 text-right">Fonte: API V2</div>
      )}
    </div>
  );
}
